import { Factory, Beaker, Package, Microscope, Warehouse, Gauge, Play } from "lucide-react";
import SEO from "@/components/SEO";

const FACILITY_VIDEOS = [
  {
    title: "Liquid Filling & Capping Line in Action",
    caption: "Automated rotary filling with in-line capping and labelling.",
    src: "/api/assets/9dvj84r2_VID-20251227-WA0017.mp4",
  },
  {
    title: "Production Floor Walkthrough",
    caption: "Inside the Forage Saharanpur facility — stainless-steel manufacturing zone.",
    src: "/api/assets/g2b25lxv_VID-20241015-WA0020.mp4",
  },
];

const SECTIONS = [
  {
    icon: Beaker,
    title: "Liquid Manufacturing Section",
    image: "https://images.pexels.com/photos/37650270/pexels-photo-37650270.jpeg",
    points: [
      "Stainless steel SS-316 mixing & blending vessels",
      "Inline homogenisation and filtration",
      "Validated CIP / SIP cleaning protocols",
      "Capacity for batches up to 5,000 L",
    ],
  },
  {
    icon: Gauge,
    title: "Automated Filling Line",
    image: "https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=1200&q=80",
    points: [
      "Automated rotary fillers for 50 ml to 5 L packs",
      "In-line capping, sealing and labelling",
      "Net-weight verification and reject systems",
      "Throughput up to 5,000 units per hour",
    ],
  },
  {
    icon: Package,
    title: "Packaging Section",
    image: "https://images.unsplash.com/photo-1581090700227-1e37b190418e?auto=format&fit=crop&w=1200&q=80",
    points: [
      "Primary and secondary packaging halls",
      "Tamper-evident & batch-coded packs",
      "Custom export-ready cartons",
      "Private label & multi-language artwork",
    ],
  },
  {
    icon: Microscope,
    title: "Quality Control Laboratory",
    image: "https://images.pexels.com/photos/8851634/pexels-photo-8851634.jpeg",
    points: [
      "HPLC, UV-VIS, AAS, FTIR & wet chemistry",
      "Microbiology and stability chambers",
      "Raw material, in-process, finished testing",
      "Retained samples per batch for traceability",
    ],
  },
  {
    icon: Warehouse,
    title: "Warehouse & Logistics",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1200&q=80",
    points: [
      "Climate-controlled raw material storage",
      "Quarantine, approved and reject zones",
      "FIFO/FEFO inventory management",
      "Bonded warehouse for export consignments",
    ],
  },
];

const CAPACITY = [
  { label: "Monthly Liquid Output", value: "50,000+ L" },
  { label: "Powder & Granulation", value: "100+ MT" },
  { label: "Production Halls", value: "6" },
  { label: "QC Lab Coverage", value: "24×7" },
];

export default function Manufacturing() {
  return (
    <>
      <SEO
        title="Veterinary Contract Manufacturing India | GMP Animal Health Manufacturing"
        description="GMP-compliant veterinary contract manufacturing in India. Forage's Saharanpur facility offers liquid manufacturing, automated filling, packaging, QC laboratory and warehousing for animal health brands and exporters."
        path="/manufacturing"
        rawTitle
      />

      {/* Header */}
      <section className="relative bg-[#0B2545] text-white py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover opacity-25" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/85 to-transparent" />
        <div className="relative container-fr">
          <span className="overline text-[#2A9D8F]">Manufacturing Facility</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 leading-tight max-w-3xl">
            A modern veterinary pharma facility built for scale and precision.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl">
            From raw material entry to finished export pallet, every step
            inside our Saharanpur facility is engineered around pharmaceutical
            discipline.
          </p>
        </div>
      </section>

      {/* Capacity */}
      <section className="bg-white border-b border-slate-200">
        <div className="container-fr px-6 md:px-12 lg:px-20 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {CAPACITY.map((c) => (
            <div key={c.label}>
              <div className="font-display text-3xl md:text-4xl font-black text-[#0B2545]">{c.value}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-2">{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Sections */}
      {SECTIONS.map((s, idx) => (
        <section key={s.title} className={`section-padding ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}>
          <div className="container-fr grid lg:grid-cols-2 gap-14 items-center">
            <div className={idx % 2 === 0 ? "" : "lg:order-2"}>
              <div className="w-12 h-12 rounded-lg bg-[#0B2545] text-[#2A9D8F] flex items-center justify-center mb-5">
                <s.icon size={22} />
              </div>
              <span className="overline">Section {idx + 1}</span>
              <h2 className="h-section mt-3">{s.title}</h2>
              <ul className="mt-7 space-y-3">
                {s.points.map((p) => (
                  <li key={p} className="flex gap-3 items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2A9D8F] mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={idx % 2 === 0 ? "" : "lg:order-1"}>
              <img src={s.image} alt={s.title} loading="lazy" className="rounded-xl object-cover w-full h-[460px] shadow-lg" />
            </div>
          </div>
        </section>
      ))}

      {/* Facility Videos */}
      <section className="section-padding bg-[#0B2545] text-white">
        <div className="container-fr">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#2A9D8F]/15 text-[#2A9D8F] flex items-center justify-center">
              <Play size={18} />
            </div>
            <span className="overline text-[#2A9D8F]">Inside Forage</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black leading-tight max-w-3xl">
            See our facility in motion.
          </h2>
          <p className="text-slate-300 mt-5 max-w-2xl">
            Real footage from the Saharanpur plant — production halls, filling lines and packaging zones, exactly as they run every day.
          </p>

          <div className="mt-12 grid md:grid-cols-2 gap-8" data-testid="manufacturing-videos">
            {FACILITY_VIDEOS.map((v) => (
              <div key={v.src} className="group">
                <div className="rounded-xl overflow-hidden bg-black shadow-2xl ring-1 ring-white/10">
                  <video
                    src={v.src}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full aspect-video object-cover bg-black"
                    data-testid={`facility-video-${v.title.split(" ").join("-").toLowerCase()}`}
                  />
                </div>
                <div className="mt-4">
                  <div className="font-display text-lg font-bold">{v.title}</div>
                  <div className="text-sm text-slate-300 mt-1">{v.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
